import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { EASE } from "./Reveal";
import { scrollToId } from "@/lib/api";

const LINES = [
  { t: "Design que", cls: "font-display" },
  { t: "comunica", cls: "font-display text-outline" },
  { t: "fé & propósito.", cls: "font-serif-i text-[#2DD4BF] normal-case" },
];

const MaskLine = ({ children, i, cls }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className={`block ${cls}`} initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, ease: EASE, delay: 0.2 + i * 0.14 }}>
      {children}
    </motion.span>
  </span>
);

const TiltCard = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [12, -12]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 14 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={reset} className="relative mx-auto w-[78%] max-w-[420px] [perspective:1200px]" data-testid="hero-3d-card">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <img src="/img/book.jpeg" alt="Capa de livro" className="absolute -left-[22%] top-[14%] w-[62%] rounded-sm border border-[#163D43] opacity-80 shadow-2xl" style={{ transform: "translateZ(-60px) rotate(-8deg)" }} />
        <img src="/img/poster.jpeg" alt="Cartaz de conferência gospel" className="relative w-full rounded-sm border border-[#2DD4BF]/30 shadow-[0_40px_120px_-20px_rgba(45,212,191,0.35)]" style={{ transform: "translateZ(40px)" }} />
        <div className="font-mono-x absolute -bottom-5 -right-3 rounded-full border border-[#2DD4BF]/40 bg-[#041214]/90 px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-teal-200 backdrop-blur" style={{ transform: "translateZ(90px)" }}>
          Cartaz · Igrejas
        </div>
      </motion.div>
    </div>
  );
};

const Badge = () => (
  <div className="absolute right-6 top-24 hidden h-28 w-28 lg:block">
    <svg viewBox="0 0 100 100" className="spin-slow h-full w-full">
      <defs><path id="c" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
      <text className="font-mono-x" fontSize="8.2" letterSpacing="2.5" fill="#99F6E4">
        <textPath href="#c">S'DESIGNER · PACK GOSPEL · DESIGN · </textPath>
      </text>
    </svg>
    <span className="absolute inset-0 m-auto h-3 w-3 rounded-full bg-[#2DD4BF]" />
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const bigS = useTransform(scrollYProgress, [0, 1], [0, -200]);
  return (
    <section id="top" ref={ref} className="grain relative min-h-[100svh] overflow-hidden bg-[#041214] pt-28" data-testid="hero-section">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[640px] w-[640px] rounded-full bg-[#0D9488]/25 blur-[140px]" />
      <div className="pointer-events-none absolute -left-40 bottom-0 h-[420px] w-[420px] rounded-full bg-[#071D1F] blur-[80px]" />
      <motion.span style={{ y: bigS }} className="font-display pointer-events-none absolute -bottom-24 right-[-4%] select-none text-[38vw] font-bold leading-none text-[#0B2326]" aria-hidden="true">
        S'
      </motion.span>
      <Badge />
      <div className="relative mx-auto grid max-w-[1400px] items-center gap-14 px-5 pb-20 md:px-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="font-mono-x mb-8 text-xs uppercase tracking-[0.25em] text-teal-300" data-testid="hero-eyebrow">
            Agência S'Designer — Design gráfico & infoprodutos
          </motion.p>
          <h1 className="text-5xl font-semibold uppercase leading-[0.92] tracking-tight text-teal-50 sm:text-6xl lg:text-[6.6rem]" data-testid="hero-title">
            {LINES.map((l, i) => (
              <MaskLine key={l.t} i={i} cls={l.cls}>{l.t}</MaskLine>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.8, ease: EASE }} className="mt-8 max-w-xl text-base leading-relaxed text-teal-100/85 md:text-lg">
            Cartazes para igrejas e eventos, artes motion, estampas, capas de livro, branding e mídia kit para artistas. E o <span className="text-[#2DD4BF]">Pack Gospel</span> — artes editáveis para designers.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8, ease: EASE }} className="mt-10 flex flex-wrap gap-4">
            <button onClick={() => scrollToId("servicos")} data-testid="hero-cta-services" className="group inline-flex items-center gap-3 rounded-full bg-[#2DD4BF] px-7 py-4 font-semibold text-[#041214] transition-transform hover:-translate-y-0.5">
              Ver serviços <ArrowDownRight className="h-5 w-5 transition-transform group-hover:rotate-[-45deg]" />
            </button>
            <button onClick={() => scrollToId("pack-gospel")} data-testid="hero-cta-pack" className="rounded-full border border-[#2DD4BF]/50 px-7 py-4 font-semibold text-teal-50 transition-colors hover:bg-[#2DD4BF]/10">
              Conhecer o Pack Gospel
            </button>
          </motion.div>
        </div>
        <motion.div style={{ y }} initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.3, ease: EASE, delay: 0.4 }} className="lg:col-span-5">
          <TiltCard />
        </motion.div>
      </div>
    </section>
  );
};
